/**
 * ApprovalPanel Component
 * AIDLC Administrative Backend
 *
 * Displays pending approval requests from agents.
 * CEO can approve, deny, or send instructions.
 * Answers go over the authenticated REST route; a card leaves only when the server accepted the
 * answer (APPROVAL-TOKEN-1, BL-beb6a8cc). Live requests and answers arrive over the WebSocket.
 */

import { useState } from 'react';
import { usePendingApprovals } from '../../hooks/usePendingApprovals';
import { ApprovalsLoadAlert } from './ApprovalsLoadAlert';
import type { ApprovalDecision } from '../../services/approvalService';

const typeLabels: Record<string, string> = {
  tool_denied: 'Permission Required',
  action_approval: 'Action Approval',
  direction_needed: 'Direction Needed',
  review_required: 'Review Required',
};

const typeColors: Record<string, string> = {
  tool_denied: 'bg-red-50 border-red-200 text-red-700',
  action_approval: 'bg-amber-50 border-amber-200 text-amber-700',
  direction_needed: 'bg-blue-50 border-blue-200 text-blue-700',
  review_required: 'bg-purple-50 border-purple-200 text-purple-700',
};

export function ApprovalPanel() {
  const { approvals, loadError, loading, retryLoad, isResponding, answerErrors, respond } = usePendingApprovals();
  const [instructionText, setInstructionText] = useState<Record<string, string>>({});

  const handleRespond = async (requestId: string, decision: ApprovalDecision) => {
    const accepted = await respond(requestId, decision, instructionText[requestId] || '');
    if (!accepted) return;
    setInstructionText((prev) => {
      const next = { ...prev };
      delete next[requestId];
      return next;
    });
  };

  if (approvals.length === 0 && !loadError) return null;

  return (
    <div className="mb-6 space-y-3">
      {loadError && (
        <ApprovalsLoadAlert message={loadError} onRetry={retryLoad} retrying={loading} className="p-3 rounded-lg text-sm" />
      )}

      {approvals.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
          </span>
          <h3 className="text-sm font-semibold text-amber-700">
            {approvals.length} Pending Approval{approvals.length !== 1 ? 's' : ''}
          </h3>
        </div>
      )}

      {approvals.map((approval) => (
        <div
          key={approval.id}
          className={`border rounded-lg p-4 ${typeColors[approval.type] || 'bg-gray-50 border-gray-200 text-gray-700'}`}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                {typeLabels[approval.type] || approval.type}
              </span>
              <h4 className="text-sm font-semibold mt-0.5">{approval.title}</h4>
            </div>
            <span className="text-[10px] opacity-60">
              {approval.agentId} | {new Date(approval.timestamp).toLocaleTimeString()}
            </span>
          </div>

          {/* Description */}
          <p className="text-xs opacity-80 mb-3">{approval.description}</p>

          {/* Context (if any) */}
          {approval.context && Object.keys(approval.context).length > 0 && (
            <pre className="text-[10px] bg-white/50 rounded p-2 mb-3 overflow-x-auto opacity-70">
              {JSON.stringify(approval.context, null, 2)}
            </pre>
          )}

          {/* Instructions input */}
          <div className="mb-3">
            <textarea
              placeholder="Send instructions to the agent (optional)..."
              value={instructionText[approval.id] || ''}
              onChange={(e) => setInstructionText((prev) => ({ ...prev, [approval.id]: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 text-xs border border-current/20 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 placeholder:opacity-50"
            />
          </div>

          {answerErrors[approval.id] && (
            <p role="alert" className="mb-2 text-xs font-medium text-red-700">
              {answerErrors[approval.id]}
            </p>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleRespond(approval.id, 'approved')}
              disabled={isResponding(approval.id)}
              className="px-4 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Approve
            </button>
            <button
              onClick={() => handleRespond(approval.id, 'denied')}
              disabled={isResponding(approval.id)}
              className="px-4 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              Deny
            </button>
            {instructionText[approval.id]?.trim() && (
              <button
                onClick={() => handleRespond(approval.id, 'instructions')}
                disabled={isResponding(approval.id)}
                className="px-4 py-1.5 text-xs font-medium text-white bg-ldaja-blue rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Send Instructions
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
